<template>
  <div class="reporting-main">
    <div class="reporting-header d-flex align-center mb-2">
      <v-btn text small class="reporting-back-btn" @click="backToConfig">
        <v-icon left>arrow_back</v-icon>
        {{ $t('reporting.config.reportingConfiguration') }}
      </v-btn>
    </div>
    <attribute-filter-panel
      class="attribute-filter-panel"
      :filter-rules="reportingAttributesFilter"
      :use-reporting-store="true"
      enable-hierarchy-filters
      title-localisation="attributes.filters.filterByAttributes"
      filter-count-localisation="attributes.filters.numApplied"
      @attributeFilterChange="setReportingFilter"
    />
    <reporting-grid />
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import { debounce } from 'lodash';
import clientConfig from '@sharedModules/config/client';
import { reportingMaxAttributeValues } from '@enums/feature-flags';
import hierarchyFiltersMixin from '@/js/mixins/hierarchyFilters';
import { rootHierarchyLevel } from '@enums/hierarchy';

export default {
  mixins: [hierarchyFiltersMixin],

  events: {
    async onCompetitorSelectionChanged() {
      if (this.debounceFetchReportingAggregations) {
        this.debounceFetchReportingAggregations();
      }
    },
  },

  beforeRouteLeave(to, from, next) {
    // Clear store group expansion when leaving the reporting page
    this.clearRowExpansion();
    next();
  },

  data() {
    return {
      debounceFetchReportingAggregations: null,
    };
  },

  computed: {
    ...mapState('reporting', ['selectedWorkpackageIds']),
    // Use reporting-specific filters instead of global retailAttributesFilter
    ...mapState('filters', ['reportingAttributesFilter']),
    ...mapState('clientConfig', ['toggleLogic']),

    maxAttributeValues() {
      return this.toggleLogic[reportingMaxAttributeValues];
    },
  },

  async created() {
    // Initialise reporting context from route (wp ids, attribute keys, approval status)
    await this.initialiseFromRoute(this.$route.query);

    // Fetch intersected attributes from backend
    // Backend returns only attributes that have values across all selected workpackages
    if (this.selectedWorkpackageIds && this.selectedWorkpackageIds.length > 0) {
      await this.fetchIntersectedAttributes({
        workpackageIds: this.selectedWorkpackageIds,
        includeValues: true,
        maxAttributeValues: this.maxAttributeValues,
      });

      // Fetch and filter hierarchy filters (must be done in frontend as hierarchy structure is client-side)
      await this.fetchAndFilterHierarchyFilters();
    }

    this.fetchReportingAggregations();

    this.debounceFetchReportingAggregations = debounce(
      this.fetchReportingAggregations,
      clientConfig.inputDebounceValue
    );
  },

  methods: {
    ...mapActions('reporting', [
      'fetchReportingAggregations',
      'initialiseFromRoute',
      'clearRowExpansion',
      'fetchIntersectedAttributeValues',
      'fetchIntersectedAttributes',
    ]),
    ...mapActions('filters', ['setSelectedFilter']),
    ...mapMutations('reporting', ['setAvailableHierarchyFilters']),

    async fetchAndFilterHierarchyFilters() {
      // Backend handles custom attribute intersection, but hierarchy filters need frontend checking
      // because the hierarchy structure and available levels are defined client-side
      const allHierarchyFilters = this.getHierarchyFilters({
        level: rootHierarchyLevel,
      });

      // Check which hierarchy filters have intersected values across selected workpackages
      const filterResults = await Promise.all(
        allHierarchyFilters.map(async hierarchyFilter => {
          try {
            const result = await this.fetchIntersectedAttributeValues({
              workpackageIds: this.selectedWorkpackageIds,
              attributeKey: hierarchyFilter.attributeKey,
            });

            const hasValues = result && Array.isArray(result) && result.length > 0;
            return hasValues ? hierarchyFilter : null;
          } catch (error) {
            // On error, include the filter (fail open)
            return hierarchyFilter;
          }
        })
      );

      // Store only hierarchy filters that have values
      this.setAvailableHierarchyFilters(filterResults.filter(f => f !== null));
    },

    setReportingFilter(attributeFilters) {
      // Use reporting-specific filter in filters store
      this.setSelectedFilter({
        filterName: 'reportingAttributesFilter',
        filterValue: attributeFilters,
      });

      if (this.debounceFetchReportingAggregations) {
        this.debounceFetchReportingAggregations();
      }
    },
    backToConfig() {
      this.$router.push({ name: 'reportingConfig', query: this.$route.query });
    },
  },
};
</script>

<style lang="scss" scoped>
.attribute-filter-panel {
  position: sticky;
  top: 112px;
  z-index: 9;
}
</style>
